import { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  getContacts,
  getConversations,
  getCustomAttributes,
  updateContactCustomAttribute,
  updateConversationCustomAttributes,
} from '../api';

// ============================================================================
// IFS patch (2026-07-21): suporte a conversation_attribute
// ============================================================================
// Antes: kanban so funcionava com contact_attribute. Cards = contatos, DnD
// atualizava contact.custom_attributes.
// Agora: se attribute_model === 'conversation_attribute', cards = conversations
// normalizadas pra ter o mesmo shape que contatos (id/name/email/phone/thumbnail/
// custom_attributes). Isso mantem KanbanCard/KanbanColumn intactos.
// DnD roteia pra endpoint correto: /contacts/{id} (PUT) ou
// /conversations/{id}/custom_attributes (POST).
// ============================================================================

// Normaliza uma conversation pro shape que KanbanCard espera (mesmo shape que contact)
function normalizeConversationToItem(conv) {
  const sender = (conv.meta && conv.meta.sender) || {};
  return {
    id: conv.id,
    __type: 'conversation',
    // Custom attributes vem da conversation (nao do sender)
    custom_attributes: conv.custom_attributes || {},
    // Nome/email/phone/thumbnail vem do sender pra card ficar legivel
    name: sender.name ? `${sender.name} · #${conv.id}` : `Conversa #${conv.id}`,
    email: sender.email || null,
    phone_number: sender.phone_number || null,
    thumbnail: sender.thumbnail || null,
    // Meta ajuda quem precisar
    _conversation_meta: {
      status: conv.status,
      inbox_id: conv.inbox_id,
      contact_id: sender.id,
    },
  };
}

function normalizeContactToItem(contact) {
  return { ...contact, __type: 'contact' };
}

export function useDynamicKanbanData(reloadFlag = 0) {
  const location = useLocation();

  // Items = contacts (se attribute e contact_attribute) OU conversations normalizadas
  const [contacts, setContacts] = useState([]);

  // Estado para armazenar o atributo customizado atual (guardando os campos essenciais + attribute_model)
  const [attribute, setAttribute] = useState(null);

  // Estado para armazenar todos os atributos do tipo lista (para dropdown)
  const [listAttributes, setListAttributes] = useState([]);

  // Estados para controle de carregamento e erros
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs para armazenar o último parâmetro da URL e o último atributo selecionado
  const lastParamRef = useRef(null);
  const lastAttributeKeyRef = useRef(null);

  // Memoização das colunas para evitar re-renderizações desnecessárias
  const columns = useMemo(() => {
    if (!attribute) return [];
    return attribute.attribute_values || [];
  }, [attribute]);

  // Memo para mapear displayNames dos atributos do tipo lista
  const displayNames = useMemo(() => {
    const map = {};
    listAttributes.forEach(attr => {
      map[attr.attribute_key] = attr.attribute_display_name || attr.attribute_key;
    });
    return map;
  }, [listAttributes]);

  useEffect(() => {
    // Extrai o parâmetro 'kbw' da URL
    const searchParams = new URLSearchParams(location.search);
    const param = searchParams.get('kbw');

    console.group('[DEBUG] useDynamicKanbanData: Efeito disparado');
    console.log('[DEBUG] Parâmetro atual da URL (kbw):', param);
    console.log('[DEBUG] Parâmetro anterior:', lastParamRef.current);

    // Evita recarregar se o parâmetro 'kbw' não mudou (mas permite reloadFlag)
    if (lastParamRef.current === param && reloadFlag === 0) {
      console.log('[DEBUG] Parâmetro igual ao anterior, efeito abortado');
      console.groupEnd();
      return;
    }
    lastParamRef.current = param;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        let attrs = [];
        let selectedAttr = null;

        // Busca todos os atributos customizados (contact + conversation)
        attrs = await getCustomAttributes();
        // Filtra atributos do tipo lista com valores validos (funcionam pra kanban)
        const validListAttrs = attrs.filter(
          a => a.attribute_display_type === 'list' && a.attribute_values && a.attribute_values.length
        );
        setListAttributes(validListAttrs);
        console.log('[DEBUG] Atributos carregados:', attrs.map(a => `${a.attribute_key} (${a.attribute_model})`));

        // Seleciona o atributo conforme o parâmetro exato da URL, se existir
        if (param) {
          selectedAttr = validListAttrs.find(a => a.attribute_key === param);
          console.log('[DEBUG] Atributo selecionado via parâmetro:', selectedAttr && selectedAttr.attribute_key);
        }

        // Fallback: primeiro attribute com prefixo kbw_ (compatibilidade original)
        if (!selectedAttr) {
          selectedAttr = validListAttrs.find(a => a.attribute_key.startsWith('kbw_'));
          console.log('[DEBUG] Atributo selecionado por fallback (kbw_):', selectedAttr && selectedAttr.attribute_key);
        }

        // Fallback final: primeiro attribute do tipo lista
        if (!selectedAttr) {
          selectedAttr = validListAttrs[0];
          console.log('[DEBUG] Atributo selecionado por fallback (qualquer lista):', selectedAttr && selectedAttr.attribute_key);
        }

        if (!selectedAttr) throw new Error('Nenhum atributo tipo lista encontrado.');

        // ==== Roteamento contact_attribute vs conversation_attribute ====
        const attrModel = selectedAttr.attribute_model || 'contact_attribute';
        let items = [];
        if (attrModel === 'conversation_attribute') {
          const convs = await getConversations();
          items = convs.map(normalizeConversationToItem);
          console.log(`[DEBUG] conversation_attribute — ${items.length} conversas carregadas`);
        } else {
          const contactsData = await getContacts();
          items = contactsData.map(normalizeContactToItem);
          console.log(`[DEBUG] contact_attribute — ${items.length} contatos carregados`);
        }
        setContacts(items);

        // Atualiza o estado do atributo apenas se mudou
        if (lastAttributeKeyRef.current !== selectedAttr.attribute_key) {
          setAttribute({
            attribute_key: selectedAttr.attribute_key,
            attribute_values: selectedAttr.attribute_values,
            attribute_model: attrModel,
          });
          lastAttributeKeyRef.current = selectedAttr.attribute_key;
          console.log('[DEBUG] Estado attribute atualizado! model=', attrModel);
        } else {
          console.log('[DEBUG] Atributo igual ao anterior; apenas items atualizados.');
        }
      } catch (err) {
        setError(err);
        console.error('[DEBUG] Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
        console.groupEnd();
      }
    }

    fetchData();
  }, [location.search, reloadFlag]);

  // Retorna os dados e estados do hook para o componente consumir
  return { contacts, columns, attribute, listAttributes, displayNames, loading, error };
}

// Hook para atualização de atributo customizado — roteia contact vs conversation
export function useUpdateContactAttribute(onSuccess, attribute, itemsFromHook) {
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const updateContactAttribute = async (itemId, attributeKey, value) => {
    const attrModel = (attribute && attribute.attribute_model) || 'contact_attribute';
    const isConversation = attrModel === 'conversation_attribute';

    // ============================================================================
    // CASO 1: apagar a chave (drop na coluna "Não definido")
    // ============================================================================
    if (typeof value === 'undefined') {
      setUpdating(true);
      setUpdateError(null);
      try {
        // Pega o item atual do hook (ja carregado) pra saber o custom_attributes atual
        let item = null;
        if (Array.isArray(itemsFromHook)) {
          item = itemsFromHook.find(c => c.id === itemId);
        }

        if (isConversation) {
          // Conversation: reenvia o custom_attributes completo sem a chave
          const currentAttrs = { ...(item && item.custom_attributes) || {} };
          delete currentAttrs[attributeKey];
          await updateConversationCustomAttributes(itemId, currentAttrs);
        } else {
          // Contact: mesma logica original — merge sem a chave
          if (!item) {
            const contacts = await getContacts();
            item = contacts.find(c => c.id === itemId);
          }
          const customAttrs = { ...(item && item.custom_attributes) || {} };
          delete customAttrs[attributeKey];
          // Compat: envia value=undefined pra sinalizar delete (comportamento original)
          await updateContactCustomAttribute(itemId, attributeKey, undefined);
          // Depois envia dict completo sem a chave pra garantir remoção
          await updateContactCustomAttribute(itemId, '', customAttrs);
        }

        if (typeof onSuccess === 'function') onSuccess();
      } catch (error) {
        setUpdateError(error);
        console.error('[DEBUG] Erro ao remover atributo:', error);
      } finally {
        setUpdating(false);
      }
      return;
    }

    // ============================================================================
    // CASO 2: setar novo valor — validar que e valor valido
    // ============================================================================
    if (
      typeof value !== 'undefined' &&
      (!Array.isArray(attribute && attribute.attribute_values) ||
        !attribute.attribute_values.includes(value))
    ) {
      const errMsg = `[DEBUG] Valor inválido para coluna: "${value}". Valores permitidos: ${JSON.stringify(
        attribute && attribute.attribute_values
      )}`;
      setUpdateError(new Error(errMsg));
      console.error(errMsg);
      return;
    }

    setUpdating(true);
    setUpdateError(null);
    try {
      if (isConversation) {
        // Merge com custom_attributes atual do item
        let item = null;
        if (Array.isArray(itemsFromHook)) {
          item = itemsFromHook.find(c => c.id === itemId);
        }
        const currentAttrs = { ...(item && item.custom_attributes) || {} };
        currentAttrs[attributeKey] = value;
        await updateConversationCustomAttributes(itemId, currentAttrs);
      } else {
        await updateContactCustomAttribute(itemId, attributeKey, value);
      }

      if (typeof onSuccess === 'function') onSuccess();
    } catch (error) {
      setUpdateError(error);
      console.error('[DEBUG] Erro ao atualizar atributo:', error);
    } finally {
      setUpdating(false);
    }
  };

  return { updateContactAttribute, updating, updateError };
}

// Hook combinado para facilitar o consumo no componente
export function useKanbanData() {
  // Estado para forçar recarregamento dos dados do Kanban
  const [reloadFlag, setReloadFlag] = useState(0);

  const { contacts, columns, attribute, listAttributes, displayNames, loading, error } =
    useDynamicKanbanData(reloadFlag);
  const reloadKanban = () => setReloadFlag(f => f + 1);
  const { updateContactAttribute, updating, updateError } = useUpdateContactAttribute(
    reloadKanban,
    attribute,
    contacts
  );

  return {
    contacts,
    columns,
    attribute,
    listAttributes,
    displayNames,
    loading,
    error,
    updateContactAttribute,
    updating,
    updateError,
    reloadKanban,
  };
}
